import { useEffect, useRef, useState } from "react";
import { Flame, Volume2, VolumeX, Play, Instagram, ChevronRight } from "lucide-react";

// A single vertical reel with autoplay-in-view, tap-to-unmute, and sound toggle.
function Reel({ r, featured = false, onSelect }) {
  const videoRef = useRef(null);
  const [inView, setInView] = useState(false);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(false);
  const poster = r.url?.startsWith("/reels/") ? r.url.replace(/\.mp4$/i, ".jpg") : undefined;

  useEffect(() => {
    const el = videoRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            el.play().then(() => setPlaying(true)).catch(() => setPlaying(false));
          } else {
            el.pause();
            setPlaying(false);
          }
        });
      },
      { threshold: 0.4 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const togglePlay = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.paused) el.play().then(() => setPlaying(true)).catch(() => {});
    else { el.pause(); setPlaying(false); }
  };
  const toggleMute = (e) => {
    e.stopPropagation();
    const el = videoRef.current;
    if (!el) return;
    el.muted = !el.muted;
    setMuted(el.muted);
  };

  const aspectClass = featured ? "aspect-[9/16]" : "aspect-[9/13]";

  return (
    <div
      className={`group relative bg-black overflow-hidden ${featured ? "ring-2 ring-[#FFB300]" : ""} cursor-pointer`}
      onClick={() => onSelect ? onSelect(r) : togglePlay()}
      data-testid={`featured-reel-${r.id}`}
    >
      <div className={`relative ${aspectClass} w-full`}>
        <video
          ref={videoRef}
          src={r.url}
          poster={poster}
          className="absolute inset-0 w-full h-full object-cover"
          muted={muted}
          loop
          playsInline
          preload={inView ? "auto" : "metadata"}
          controls={false}
        />

        {/* Top-left platform tag */}
        <div className="absolute top-3 left-3 bg-black/70 backdrop-blur text-white text-[10px] uppercase tracking-widest px-2 py-1 font-heading flex items-center gap-1">
          <Flame size={11} className="text-[#FFB300]" /> TikTok
        </div>

        {/* Featured badge */}
        {featured && (
          <div className="absolute top-3 right-3 bg-[#FFB300] text-[#1A1A1A] text-[10px] uppercase tracking-widest px-2 py-1 font-heading" data-testid="featured-reel-badge">
            Trending
          </div>
        )}

        {/* Play indicator when paused (visible on hover for any reel) */}
        {!playing && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/25">
            <div className="bg-white/95 text-[#1A1A1A] w-14 h-14 rounded-full flex items-center justify-center shadow-xl">
              <Play size={22} className="ml-1" fill="currentColor" />
            </div>
          </div>
        )}

        {/* Mute toggle */}
        <button
          onClick={toggleMute}
          className="absolute bottom-3 right-3 bg-black/70 backdrop-blur text-white w-9 h-9 flex items-center justify-center hover:bg-[#D32F2F] transition-colors rounded-full"
          aria-label={muted ? "Unmute" : "Mute"}
          data-testid={`reel-mute-${r.id}`}
        >
          {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
        </button>

        {/* Bottom overlay with title */}
        <div className="absolute bottom-0 inset-x-0 p-3 bg-gradient-to-t from-black/85 via-black/50 to-transparent">
          <div className="font-heading text-white text-sm md:text-base uppercase tracking-wide leading-tight line-clamp-2">{r.title}</div>
          {r.author && <div className="text-[10px] text-white/70 mt-0.5">{r.author}</div>}
        </div>
      </div>
    </div>
  );
}

export function FeaturedReels({ reviews }) {
  const videoReels = (reviews || []).filter(
    (r) => r.platform === "video" || /\.(mp4|webm|mov)(\?|$)/i.test(r.url || "")
  );
  if (videoReels.length === 0) return null;

  const [featured, ...rest] = videoReels;
  const grid = rest.slice(0, 6);

  return (
    <section className="bg-[#1A1A1A] text-[#FFFDF8] relative overflow-hidden" data-testid="home-reviews">
      {/* Ambient glow */}
      <div className="absolute -top-40 -left-40 w-96 h-96 bg-[#D32F2F] opacity-20 blur-3xl rounded-full pointer-events-none" />
      <div className="absolute -bottom-40 -right-40 w-96 h-96 bg-[#FFB300] opacity-15 blur-3xl rounded-full pointer-events-none" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24 relative">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10">
          <div>
            <div className="inline-flex items-center gap-2 bg-[#FFB300] text-[#1A1A1A] px-3 py-1.5 text-[10px] tracking-[0.3em] uppercase font-heading">
              <Flame size={12} /> As seen on TikTok
            </div>
            <h2 className="font-heading text-5xl md:text-7xl uppercase tracking-tight leading-[0.9] mt-4">
              Watch the<br /><span className="text-[#FFB300]">Chamoy</span> go viral.
            </h2>
            <p className="text-white/75 mt-4 max-w-lg text-lg leading-relaxed">
              Real customers. Real chile. Every reel is a real bite of Alekas Treats — filmed by shop owners, snack heads and TikTok fans across the U.S.
            </p>
          </div>
          <a
            href="https://www.tiktok.com/@alekastreatsllc"
            target="_blank"
            rel="noreferrer"
            className="group inline-flex items-center gap-3 bg-white text-[#1A1A1A] hover:bg-[#FFB300] px-6 py-4 uppercase tracking-widest text-sm font-heading transition-colors"
            data-testid="home-reels-tiktok-link"
          >
            <span>Follow @alekastreatsllc</span>
            <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
          </a>
        </div>

        {/* Featured + Grid layout */}
        <div className="grid md:grid-cols-3 gap-4 md:gap-6">
          {/* Featured reel — takes 1 col on desktop */}
          <div className="md:col-span-1">
            <Reel r={featured} featured />
          </div>

          {/* Grid of smaller reels — 2 cols on desktop, 2/3 columns on mobile */}
          <div className="md:col-span-2 grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6">
            {grid.map((r) => (
              <Reel key={r.id} r={r} />
            ))}
          </div>
        </div>

        {/* Footer strip */}
        <div className="mt-10 grid md:grid-cols-3 gap-6 border-t border-white/10 pt-8">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFB300]">Trending Now</div>
            <div className="font-heading text-2xl uppercase tracking-wide mt-1 leading-tight">{videoReels.length} viral reels</div>
            <div className="text-white/60 text-sm mt-1">Updated weekly — new drops every Monday.</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFB300]">Real Reviews</div>
            <div className="font-heading text-2xl uppercase tracking-wide mt-1 leading-tight">100% authentic</div>
            <div className="text-white/60 text-sm mt-1">Uploaded by real customers, not paid promo.</div>
          </div>
          <div className="flex flex-col justify-center gap-3">
            <a
              href="https://www.instagram.com/alekastreatsllc"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 text-sm uppercase tracking-widest text-white hover:text-[#FFB300]"
              data-testid="home-reels-instagram-link"
            >
              <Instagram size={16} /> Follow on Instagram
            </a>
            <a
              href="/shop"
              className="inline-flex items-center gap-2 bg-[#D32F2F] hover:bg-[#B71C1C] px-5 py-3 uppercase tracking-widest text-xs font-heading text-white transition-transform hover:-translate-y-1 w-fit"
              data-testid="home-reels-shop-cta"
            >
              Shop what&apos;s trending <ChevronRight size={14} />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
